// externals
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// components
import { Button } from '@interstate/components/Button';
import { Typography } from '@interstate/components/Typography';

// selectors
import { CDMS, DMS_PLUS } from '@makemydeal/dr-dash-store/constants';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as pushToDmsActionCreators from '@makemydeal/dr-dash-store/actions/pushToDmsActionCreators';
import * as pushToDmsSelectors from '@makemydeal/dr-dash-store/selectors/pushToDmsSelectors';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import ConfirmPushToDmsDialogInterstate from '../pushToDms/confirmPushToDmsDialog/ConfirmPushToDmsDialog.interstate';
import { logCdmsNR } from '@makemydeal/dr-dash-store/utils/cdmsNrUtils';

// constants
import { createPortal } from 'react-dom';
import { PushToDmsStatus } from '@makemydeal/dr-dash-types';
import { useLastPushToDmsUpdateText, usePushToDmsButtonText } from '../pushToDms/pushToDmsHooks';
import {
    PUSH_DEAL_CANCEL_EVENT,
    PUSH_DEAL_CLOSE_ERROR_EVENT,
    PUSH_DEAL_FAILED_EVENT,
    PUSH_DEAL_SUCCESS_EVENT,
    PUSH_DEAL_RESPONSE_EVENT,
    PUSH_DEAL_CLICK_EVENT,
    PUSH_DEAL_LOADED_EVENT
} from '../pushToDms/pushToDmsConstants';
import PushToDmsLoaderDialog from '../pushToDms/pushToDmsLoaderDialog/PushToDmsLoaderDialog';
import PushToDmsDialog from '../pushToDms/pushToDmsDialog/PushToDmsDialog.interstate';

const PushToDms = forwardRef(({ isBaseScreen = false }: { isBaseScreen?: boolean }, ref) => {
    const [showConfirmPushToDmsDialog, setConfirmPushToDmsDialog] = useState(false);
    const [showPushToDmsDialog, setShowPushToDmsDialog] = useState(false);
    const [pushToDmsStatus, setPushToDmsStatus] = useState(PushToDmsStatus.Ready);
    const [dataEntryUrl, setDataEntryUrl] = useState<string | undefined>();
    const [hasBeenPushed, setHasBeenPushed] = useState<{ time: string | Date }>({
        time: ''
    });
    const [showLoaderDialog, setShowLoaderDialog] = useState(false);
    const [isIframeClosedAfterCdmsPush, setIsIframeClosedAfterCdmsPush] = useState(false);

    const offerSaved = useSelector(offerInfoSelectors.getHasBeenSaved);
    const returnedSuccessfulCdmsResponse = useSelector(pushToDmsSelectors.getReturnedSuccessfulCdmsResponse);
    const cdmsResponseData = useSelector(pushToDmsSelectors.getCdmsResponseData);
    const enableOptionalPbcUi = useSelector(featureToggleSelectors.enableOptionalPbcUi);
    const enableCdmsSyncFlow = useSelector(featureToggleSelectors.enableCdmsSyncFlow);
    const isDMSEnabled = useSelector(dealerSelectors.getDMSIntegrationToggle).toLowerCase() !== 'off';
    const vehicleStockNumber = useSelector(vehicleSelectors.getVehicleStockNumber);
    const lastSucceedDmsDealId = useSelector(pushToDmsSelectors.getLastSucceededDmsDealId);
    const cdmsStorefrontErrorMessage = useSelector(pushToDmsSelectors.getCdmsStorefrontErrorMessage);
    const pushToDmsWsEvent = useSelector(pushToDmsSelectors.getPushToDmsWsEvent);
    const dmsIntegrationToggle = useSelector(dealerSelectors.getDMSIntegrationToggle);
    const cdmsPbcUrl = useSelector(configSelectors.getCDMSPbcUrl);

    const [isPushToCdmsActive, setIsPushToCdmsActive] = useState(false);
    const [isExtendedLoader, setIsExtendedLoader] = useState(false);

    const sendToDMSText = useLastPushToDmsUpdateText(hasBeenPushed.time);
    const pushToDmsButtonText = usePushToDmsButtonText();
    const isCDMSIntegration = dmsIntegrationToggle === CDMS;

    useEffect(() => {
        if (enableOptionalPbcUi) window.addEventListener('message', pbcPostbackMessageListener);

        return () => {
            if (enableOptionalPbcUi) {
                window.removeEventListener('message', pbcPostbackMessageListener);
            }
        };
    }, []);

    useEffect(() => {
        const isValidCdmsResponseData =
            cdmsResponseData && typeof cdmsResponseData === 'object' && Object.keys(cdmsResponseData).length > 0;
        if (isCDMSIntegration && enableOptionalPbcUi && isPushToCdmsActive && isValidCdmsResponseData) {
            const dataEntryURL = cdmsResponseData.dataEntryURL;
            const parsedDataEntryUrl = typeof dataEntryURL === 'string' && dataEntryURL.trim();
            if (parsedDataEntryUrl) {
                logCdmsNR('Optional UI Flow', 'success', cdmsResponseData);
                setDataEntryUrl(parsedDataEntryUrl);
            } else {
                logCdmsNR('Optional UI Bypass', 'success', cdmsResponseData);
                returnedSuccessfulCdmsResponse && setIsExtendedLoader(true);
                setIsIframeClosedAfterCdmsPush(true);
            }
        }
    }, [cdmsResponseData]);

    useEffect(() => {
        const dmsDealId = pushToDmsWsEvent.payload?.dms_deal_id;
        const hasWsEventPayload = !!(dmsDealId || (Object.keys(pushToDmsWsEvent).length && !dmsDealId));
        if (enableOptionalPbcUi && isIframeClosedAfterCdmsPush && isPushToCdmsActive && hasWsEventPayload) {
            handleWaitingLoaderPushToDmsClose();
            if (dmsDealId) {
                logCdmsNR('WS Event', 'success', pushToDmsWsEvent);
                handleConfirmPushToDmsSaved();
            } else {
                logCdmsNR('WS Event', 'failure', pushToDmsWsEvent);
                handleConfirmPushToDmsFailed();
            }
            setIsIframeClosedAfterCdmsPush(false);
        } else if (hasWsEventPayload) {
            logCdmsNR('WS Event', 'skipped', {
                enableOptionalPbcUi,
                isIframeClosedAfterCdmsPush,
                isPushToCdmsActive,
                hasDmsDealId: !!dmsDealId
            });
        }
    }, [pushToDmsWsEvent, isIframeClosedAfterCdmsPush]);

    useImperativeHandle(ref, () => {
        return isBaseScreen ? { handleConfirmPushToDmsShow } : {};
    });

    const handleConfirmPushToDmsShow = () => {
        setConfirmPushToDmsDialog(true);

        if (enableOptionalPbcUi && isCDMSIntegration) {
            setShowPushToDmsDialog(false);
            dispatch(pushToDmsActionCreators.cleanPushToDmsWsEvent());
            dispatch(pushToDmsActionCreators.cleanPushToCdmsErrorText());
        }
    };

    const handleConfirmPushToDmsSaved = () => {
        setConfirmPushToDmsDialog(false);
        setShowPushToDmsDialog(true);
        setPushToDmsStatus(PushToDmsStatus.Ready);
        setHasBeenPushed(Object.assign({}, { ...hasBeenPushed }, { time: new Date() }));
    };

    const handleConfirmPushToDmsFailed = () => {
        /* istanbul ignore next */
        if (cdmsStorefrontErrorMessage) {
            logCdmsNR('CdmsApi', 'failure', { cdmsResponseData, cdmsStorefrontErrorMessage });
        }
        setConfirmPushToDmsDialog(false);
        setShowPushToDmsDialog(true);
        setPushToDmsStatus(PushToDmsStatus.Failure);
    };

    const handleConfirmPushToDmsCancel = () => {
        setConfirmPushToDmsDialog(false);
        setIsPushToCdmsActive(false);
    };

    const isModalBackdropClicked = (event: any) => {
        return event?.target?.classList?.contains('MuiDialog-container');
    };

    const handleConfirmPushToDmsHide: any = (event: any) => {
        if (isCDMSIntegration && enableOptionalPbcUi && isModalBackdropClicked(event)) {
            return;
        }
        setConfirmPushToDmsDialog(false);
    };

    const handlePushToDmsHide: any = (event: any) => {
        if (isCDMSIntegration && enableOptionalPbcUi && isModalBackdropClicked(event)) {
            return;
        }
        setShowPushToDmsDialog(false);
        setIsPushToCdmsActive(false);
    };

    const handlePushToDmsDone = () => {
        setShowPushToDmsDialog(false);
        setIsPushToCdmsActive(false);
    };

    const handleWaitingLoaderPushToDmsShow = () => {
        setShowLoaderDialog(true);
    };

    const handleWaitingLoaderPushToDmsClose = () => {
        setShowLoaderDialog(false);
    };

    const handlePushToDmsLoaderClose = () => {
        setShowLoaderDialog(false);
        setIsPushToCdmsActive(false);
    };

    const dispatch = useDispatch();

    const getDealIdentityText = () => {
        if (dmsIntegrationToggle === DMS_PLUS && offerSaved) {
            return `Stock # ${vehicleStockNumber}`;
        } else if (dmsIntegrationToggle === CDMS) {
            if (!enableOptionalPbcUi && offerSaved && returnedSuccessfulCdmsResponse) {
                return `Stock # ${vehicleStockNumber}`;
            } else {
                return `Deal ID: ${lastSucceedDmsDealId}`;
            }
        }
        return '';
    };

    const IframeWithPbcComponent = createPortal(
        <iframe
            src={`${dataEntryUrl}&hideConfirmationMsg=true`}
            width="100%"
            height="100%"
            data-testid="cdms-dataentry-iframe"
            id="cdms-dataentry-iframe"
            onLoad={handleWaitingLoaderPushToDmsClose}
            style={{
                inset: 0,
                position: 'fixed',
                zIndex: 1305
            }}
        />,
        document.getElementById('cdms-pbc-portal') as HTMLElement
    );

    const pbcPostbackMessageListener = (message: MessageEvent) => {
        if (message.origin === cdmsPbcUrl && message.data) {
            switch (message.data.type) {
                case PUSH_DEAL_SUCCESS_EVENT:
                    logCdmsNR('Postback  Success', 'success');
                    if (!enableCdmsSyncFlow) {
                        setDataEntryUrl(undefined);
                        setIsExtendedLoader(true);
                        setShowLoaderDialog(true);
                        setIsIframeClosedAfterCdmsPush(true);
                        break;
                    }
                    handleConfirmPushToDmsSaved();
                    setDataEntryUrl(undefined);
                    break;
                case PUSH_DEAL_CANCEL_EVENT:
                case PUSH_DEAL_CLOSE_ERROR_EVENT:
                    logCdmsNR('Postback  Cancel', 'cancel');
                    setShowLoaderDialog(false);
                    setIsIframeClosedAfterCdmsPush(true);
                    setDataEntryUrl(undefined);
                    setIsPushToCdmsActive(false);
                    break;
                case PUSH_DEAL_FAILED_EVENT:
                    logCdmsNR('Postback  Failure', 'failure');
                    if (!enableCdmsSyncFlow) {
                        setDataEntryUrl(undefined);
                        setIsIframeClosedAfterCdmsPush(true);
                        break;
                    }
                    setDataEntryUrl(undefined);
                    handleConfirmPushToDmsFailed();
                    break;
                case PUSH_DEAL_RESPONSE_EVENT: {
                    logCdmsNR('Postback  Response', 'response', message);
                    if (enableCdmsSyncFlow) dispatch(pushToDmsActionCreators.pushToDmsResponse(message.data.response));
                    break;
                }
                case PUSH_DEAL_CLICK_EVENT: {
                    logCdmsNR('Push DMS Clicked', 'dms clicked');
                    break;
                }
                case PUSH_DEAL_LOADED_EVENT: {
                    logCdmsNR('DMS Loaded', 'dms loaded');
                    break;
                }
            }
        }
    };

    return (
        <>
            {isDMSEnabled && !isBaseScreen && (
                <Button
                    block
                    sx={{ backgroundColor: 'transparent' }} // TODO: Waiting for transparent background on secondary buttons
                    className="interstate"
                    buttonStyle="secondary"
                    data-testid="push-to-dms-button"
                    size="medium"
                    onClick={handleConfirmPushToDmsShow}
                >
                    {pushToDmsButtonText}
                </Button>
            )}
            {sendToDMSText && !isBaseScreen && (
                <>
                    <Typography
                        variant="body-sm"
                        color="sem.color.on-surface.subdued"
                        tag="div"
                        sx={{
                            marginTop: '16px'
                        }}
                        data-testid="last-pushed-time-string"
                    >
                        {sendToDMSText}
                    </Typography>
                    <Typography
                        variant="strong-md"
                        color="sem.color.on-surface.default"
                        tag="div"
                        sx={{
                            marginTop: '4px'
                        }}
                    >
                        {getDealIdentityText()}
                    </Typography>
                </>
            )}
            <ConfirmPushToDmsDialogInterstate
                show={showConfirmPushToDmsDialog}
                data-testid="confirm-push-to-dms-dialog"
                onSaved={handleConfirmPushToDmsSaved}
                onFailed={handleConfirmPushToDmsFailed}
                onHide={handleConfirmPushToDmsHide}
                onCancel={handleConfirmPushToDmsCancel}
                showLoader={handleWaitingLoaderPushToDmsShow}
                closeLoader={handleWaitingLoaderPushToDmsClose}
                setIsPushToCdmsActive={setIsPushToCdmsActive}
                setIsExtendedLoader={setIsExtendedLoader}
            />
            {enableOptionalPbcUi && showLoaderDialog && isPushToCdmsActive && (
                <PushToDmsLoaderDialog
                    handlePushToDmsLoaderClose={handlePushToDmsLoaderClose}
                    isExtendedLoader={isExtendedLoader}
                    show={showLoaderDialog}
                />
            )}
            <PushToDmsDialog
                data-testid="push-to-dms-dialog"
                show={showPushToDmsDialog}
                status={pushToDmsStatus}
                onHide={handlePushToDmsHide}
                onDone={handlePushToDmsDone}
                isPushToCdmsActive={isPushToCdmsActive}
            />
            {isCDMSIntegration && isPushToCdmsActive && dataEntryUrl && enableOptionalPbcUi && IframeWithPbcComponent}
        </>
    );
});

export default PushToDms;
